import { MODULES, accessSummary, isGroupNameTaken, levelsFromGroups, membersOf, type Group } from '@titan/shared';
import { SHIPPED_MODULES } from '@titan/modules';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useSession } from '../../session/SessionProvider.js';
import {
  Actions,
  Button,
  ChipSelector,
  ConfirmButton,
  EmptyState,
  Field,
  InlineError,
  InlineInfo,
  ListContainer,
  ListHeader,
  ListRow,
  MultiChipSelector,
  Muted,
  Pill,
  PillRow,
  PrimaryCell,
  RowMeta,
  RowTitle,
  SecondaryCell,
  TextInput,
  WarningPanel,
} from '../../ui/index.js';
import { errorMessage } from '../useLoad.js';
import type { AccessCenterProps } from './AccessCenter.js';
import { useApprovalNotice } from './useNotice.js';
import styles from './Access.module.css';

/** Groups: people belong here; roles are attached here. Membership is managed on the group. */
export function GroupsTab({ tenant, users, groups, roles, staffTenantId, canWrite, onChanged }: AccessCenterProps) {
  const { api } = useSession();
  const q = staffTenantId ? `?tenantId=${encodeURIComponent(staffTenantId)}` : '';
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const taken = isGroupNameTaken(name, groups);
  const { notice, check, clear } = useApprovalNotice();

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    clear();
    try {
      check(await work());
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const create = () =>
    run(async () => {
      await api(`/org/groups${q}`, { method: 'POST', body: JSON.stringify({ name: name.trim(), roleIds }) });
      setName('');
      setRoleIds([]);
    });

  return (
    <div className={styles.stack}>
      {error && <InlineError>{error}</InlineError>}
      {notice && <InlineInfo>{notice}</InlineInfo>}
      <ListContainer>
        <ListHeader title="Groups" description="People belong to groups; groups hold roles. A person’s access is the highest level across their groups’ roles." />
        {canWrite && (
          <div className={styles.bar}>
            <div className={styles.barRow}>
              <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Finance team" aria-label="New group name" invalid={taken} />
              <Button onClick={create} disabled={busy || !name.trim() || taken}>
                Create group
              </Button>
            </div>
            <Field label="Roles in the new group" chips>
              <MultiChipSelector aria-label="Roles" options={roles.map((r) => ({ value: r.id, label: r.name }))} values={roleIds} onChange={setRoleIds} />
            </Field>
            {taken && <span className={styles.note}>A group with that name already exists.</span>}
          </div>
        )}
        {groups.length === 0 && <EmptyState>No groups yet.{canWrite ? ' Create one above, then add people to it.' : ''}</EmptyState>}
        {groups.map((g) => (
          <GroupRow key={g.id} group={g} open={openId === g.id} onToggle={() => setOpenId(openId === g.id ? null : g.id)} {...{ tenant, users, groups, roles, q, canWrite, busy, run }} />
        ))}
      </ListContainer>
    </div>
  );
}

function GroupRow({
  group,
  open,
  onToggle,
  tenant,
  users,
  groups,
  roles,
  q,
  canWrite,
  busy,
  run,
}: Pick<AccessCenterProps, 'tenant' | 'users' | 'groups' | 'roles'> & {
  group: Group;
  open: boolean;
  onToggle: () => void;
  q: string;
  canWrite: boolean;
  busy: boolean;
  run: (work: () => Promise<unknown>) => Promise<void>;
}) {
  const { api } = useSession();
  const [draftRoles, setDraftRoles] = useState<string[] | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const members = membersOf(group.id, users);
  const nonMembers = users.filter((u) => !u.isSuperadmin && !u.groupIds.includes(group.id));
  const roleNames = group.roleIds.map((id) => roles.find((r) => r.id === id)?.name).filter(Boolean);
  const grants = accessSummary(levelsFromGroups([group.id], groups, roles), SHIPPED_MODULES);
  const rolesDraft = draftRoles ?? group.roleIds;
  const rolesChanged = draftRoles !== null && (draftRoles.length !== group.roleIds.length || draftRoles.some((r) => !group.roleIds.includes(r)));

  const setMembership = (userId: string, groupIds: string[]) => run(() => api(`/org/users/${userId}${q}`, { method: 'PATCH', body: JSON.stringify({ groupIds }) }));

  return (
    <>
      <ListRow center clickable onClick={onToggle} onKeyDown={(e) => e.key === 'Enter' && onToggle()} aria-expanded={open}>
        <PrimaryCell>
          <RowTitle small>{group.name}</RowTitle>
          <RowMeta>{group.description ?? (roleNames.length ? `Roles: ${roleNames.join(', ')}` : 'No roles yet')}</RowMeta>
        </PrimaryCell>
        <SecondaryCell size="xl">
          <span className={styles.summaryText}>
            {grants.full.length || grants.read.length
              ? [grants.full.length ? `Full: ${grants.full.map((k) => MODULES[k].label).join(', ')}` : '', grants.read.length ? `Read only: ${grants.read.map((k) => MODULES[k].label).join(', ')}` : '', grants.management === 'full' ? 'Manages people' : ''].filter(Boolean).join(' · ')
              : grants.management === 'full'
                ? 'Manages people'
                : SHIPPED_MODULES.length === 0
                  ? 'No product module has shipped yet'
                  : 'Grants nothing yet'}
          </span>
        </SecondaryCell>
        <SecondaryCell>
          <RowMeta>
            {members.length} {members.length === 1 ? 'member' : 'members'}
          </RowMeta>
        </SecondaryCell>
        <ChevronDown size={15} strokeWidth={1.75} aria-hidden="true" className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')} />
      </ListRow>
      {open && (
        <div className={styles.detail}>
          <div className={styles.detailBlock}>
            <span className={styles.detailTitle}>Roles</span>
            <MultiChipSelector aria-label={`Roles in ${group.name}`} options={roles.map((r) => ({ value: r.id, label: r.name }))} values={rolesDraft} onChange={setDraftRoles} disabled={!canWrite || busy} />
            {canWrite && rolesChanged && (
              <div className={styles.actionsWrap}>
                <Button size="sm" disabled={busy} onClick={() => run(() => api(`/org/groups/${group.id}${q}`, { method: 'PATCH', body: JSON.stringify({ roleIds: rolesDraft }) })).then(() => setDraftRoles(null))}>
                  Save roles
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setDraftRoles(null)}>
                  Discard
                </Button>
              </div>
            )}
            {rolesChanged && accessSummary(levelsFromGroups([], groups, roles)).management === 'none' && levelsFromGroupsDraft(rolesDraft, roles) === 'full' && grants.management !== 'full' && (
              <WarningPanel>With these roles, every member of {group.name} will be able to manage people.</WarningPanel>
            )}
          </div>
          <div className={styles.detailBlock}>
            <span className={styles.detailTitle}>Members</span>
            {members.length === 0 && <Muted small>Nobody in this group yet.</Muted>}
            {members.map((m) => (
              <div key={m.id} className={styles.memberRow}>
                <span>
                  {m.name} <Muted small>· {m.email}</Muted>
                </span>
                {canWrite && (
                  <Button variant="link" size="sm" disabled={busy} onClick={() => setMembership(m.id, m.groupIds.filter((g) => g !== group.id))}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {canWrite && nonMembers.length > 0 && (
              <>
                <Muted small>Add someone</Muted>
                <ChipSelector
                  aria-label={`Add to ${group.name}`}
                  options={nonMembers.map((u) => ({ value: u.id, label: u.name }))}
                  value={adding}
                  onChange={(id) => {
                    setAdding(id);
                    const u = nonMembers.find((x) => x.id === id);
                    if (u) void setMembership(u.id, [...u.groupIds, group.id]).then(() => setAdding(null));
                  }}
                  disabled={busy}
                />
              </>
            )}
          </div>
          {canWrite && (
            <div className={styles.detailBlock}>
              <span className={styles.detailTitle}>Danger zone</span>
              <PillRow>
                {members.length > 0 && <Pill tone="warning">{members.length} {members.length === 1 ? 'person loses' : 'people lose'} these roles</Pill>}
              </PillRow>
              <Actions>
                <ConfirmButton
                  label="Delete group"
                  variant="destructive"
                  tone="critical"
                  disabled={busy}
                  message={members.length > 0 ? `${members.length} ${members.length === 1 ? 'person loses' : 'people lose'} the roles this group grants: ${roleNames.join(', ') || 'none'}. Their other groups are unaffected.` : `${group.name} has no members. Deleting it changes nobody’s access.`}
                  confirmLabel="Delete"
                  onConfirm={() => run(() => api(`/org/groups/${group.id}${q}`, { method: 'DELETE' }))}
                />
              </Actions>
              <Muted small>Seats at {tenant.name}: {users.length} of {tenant.seats}.</Muted>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** Management level a set of roles would grant. */
function levelsFromGroupsDraft(roleIds: readonly string[], roles: AccessCenterProps['roles']): 'none' | 'read' | 'full' {
  let level: 'none' | 'read' | 'full' = 'none';
  for (const r of roles) {
    if (!roleIds.includes(r.id)) continue;
    if (r.levels.management === 'full') return 'full';
    if (r.levels.management === 'read') level = 'read';
  }
  return level;
}
